import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import { WorkingDayRow, WorkingHoursSettings, browserTimeZone, timeZoneOptions, workingDayRows, workingHoursFromRows } from '@/cloud/models/working-hours.models';
import { WorkingDaysEditorComponent, updateRow } from '@/cloud/components/working-hours/working-days-editor.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { TagModule } from 'primeng/tag';

/**
 * Tenant working hours for room analytics: a switch, the time zone and opening hours per weekday. Occupancy
 * statistics on the room Insights tab (and the MCP room_utilisation tool) then only count time inside them.
 */
@Component({
    selector: 'app-namespace-working-hours',
    standalone: true,
    imports: [DatePipe, FormsModule, ButtonModule, MessageModule, SelectModule, TagModule, ToggleSwitchModule, WorkingDaysEditorComponent, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <app-section-card [title]="t()('workingHours.title')" [subtitle]="t()('workingHours.help')" icon="pi pi-clock">
            @if (loaded() && !loadFailed()) {
                <p-tag badge [value]="t()(enabled() ? 'workingHours.badgeOn' : 'workingHours.badgeOff')" [severity]="enabled() ? 'success' : 'secondary'" [rounded]="true" />
            }
            <div class="ns-form" style="max-width: 44rem">
                <p class="ns-meta">{{ t()('workingHours.overrideHint') }}</p>

                @if (loadFailed()) {
                    <p-message severity="warn">{{ t()('workingHours.loadError') }}</p-message>
                } @else if (loaded()) {
                    <div class="ns-switch">
                        <p-toggleswitch [ngModel]="enabled()" (ngModelChange)="setEnabled($event)" [disabled]="saving() || !canManage()" inputId="working-hours-enabled" />
                        <label for="working-hours-enabled">{{ t()('workingHours.enable') }}</label>
                    </div>

                    @if (enabled()) {
                        <div class="ns-field">
                            <label for="working-hours-zone">{{ t()('workingHours.timeZone') }}</label>
                            <p-select inputId="working-hours-zone" class="w-full" [options]="zones()" [ngModel]="timeZone()" (ngModelChange)="timeZone.set($event)" [filter]="true" [disabled]="!canManage()" appendTo="body" />
                        </div>

                        <app-working-days-editor idPrefix="tenant-wh" [rows]="rows()" (rowsChange)="rows.set($event)" [disabled]="!canManage()" [label]="t()('workingHours.title')" />

                        @if (error(); as key) {
                            <p-message severity="error">{{ t()(key) }}</p-message>
                        }
                        @if (canManage()) {
                            <div class="ns-actions">
                                <p-button [label]="t()('workingHours.save')" icon="fa fa-check" (onClick)="save()" [loading]="saving()" [disabled]="saving() || !!error() || !dirty()" />
                            </div>
                        }
                    }

                    @if (settings()?.changedUtc; as changed) {
                        <p class="ns-meta">{{ t()('workingHours.lastChanged') }} {{ changed | date: 'medium' }}</p>
                    }
                    @if (!canManage()) {
                        <p class="ns-meta">{{ t()('workingHours.adminOnly') }}</p>
                    }
                }
            </div>
        </app-section-card>
    `
})
export class NamespaceWorkingHours {
    private api = inject(CloudApiService);
    private messages = inject(MessageService);
    private translation = inject(TranslationService);
    readonly t = this.translation.t;

    readonly namespaceId = input.required<string>();

    readonly settings = signal<WorkingHoursSettings | null>(null);
    readonly loaded = signal(false);
    readonly loadFailed = signal(false);
    readonly saving = signal(false);
    readonly enabled = signal(false);
    readonly timeZone = signal(browserTimeZone());
    readonly rows = signal<WorkingDayRow[]>(workingDayRows(null));

    readonly canManage = computed(() => this.api.hasCapability(this.namespaceId(), 'namespace.manage'));
    readonly zones = computed(() => timeZoneOptions(this.timeZone()));
    private readonly draft = computed(() => workingHoursFromRows(this.timeZone(), this.rows()));
    readonly error = computed(() => {
        const draft = this.draft();
        return typeof draft === 'string' ? draft : null;
    });
    /** Whether the form differs from what is stored (compared in the form's own shape). */
    readonly dirty = computed(() => {
        const stored = this.settings()?.workingHours;
        if (!stored) return true;
        return JSON.stringify(this.draft()) !== JSON.stringify(workingHoursFromRows(stored.timeZone, workingDayRows(stored)));
    });

    private request = 0;

    constructor() {
        effect(() => {
            const id = this.namespaceId();
            untracked(() => void this.load(id));
        });
    }

    update(index: number, change: Partial<WorkingDayRow>): void {
        this.rows.update((rows) => updateRow(rows, index, change));
    }

    /** Switching on shows the form (saved with Save); switching off clears stored hours straight away. */
    async setEnabled(enabled: boolean): Promise<void> {
        this.enabled.set(enabled);
        if (enabled || !this.settings()?.workingHours) return;
        const id = this.namespaceId();
        this.saving.set(true);
        try {
            this.apply(await this.api.clearNamespaceWorkingHours(id));
            this.messages.add({ severity: 'success', summary: this.t()('workingHours.cleared') });
        } catch (err) {
            this.enabled.set(true);
            this.toastError(err);
        } finally {
            this.saving.set(false);
        }
    }

    async save(): Promise<void> {
        const draft = this.draft();
        if (typeof draft === 'string' || this.saving()) return;
        this.saving.set(true);
        try {
            this.apply(await this.api.setNamespaceWorkingHours(this.namespaceId(), draft));
            this.messages.add({ severity: 'success', summary: this.t()('workingHours.saved') });
        } catch (err) {
            this.toastError(err);
        } finally {
            this.saving.set(false);
        }
    }

    private async load(id: string): Promise<void> {
        const request = ++this.request;
        this.loaded.set(false);
        this.loadFailed.set(false);
        try {
            const settings = await this.api.getNamespaceWorkingHours(id);
            if (request === this.request) this.apply(settings);
        } catch {
            if (request === this.request) this.loadFailed.set(true);
        } finally {
            if (request === this.request) this.loaded.set(true);
        }
    }

    private apply(settings: WorkingHoursSettings): void {
        this.settings.set(settings);
        const hours = settings.workingHours;
        this.enabled.set(!!hours);
        this.timeZone.set(hours?.timeZone ?? browserTimeZone());
        this.rows.set(workingDayRows(hours));
    }

    private toastError(err: unknown): void {
        const e = err as { error?: { error?: string } | string; message?: string };
        const detail = (typeof e?.error === 'object' ? e.error?.error : e?.error) ?? e?.message;
        this.messages.add({ severity: 'error', summary: this.t()('workingHours.saveError'), detail });
    }
}
